import { useEffect, useState, useCallback } from 'react'
import { wsService, WebSocketMessage } from '@/services/websocket'
import type { TaskQueue, TaskStep, StepStatus } from '@/components/tasks/TaskTracker'

interface TaskQueueData {
  id: string
  user_id: string
  title: string
  steps: TaskStep[]
  status: StepStatus
  created_at: string
  updated_at: string
  conversation_id?: string
  progress: number
}

interface StepEventData {
  queue_id: string
  step?: TaskStep
  step_id?: string
  progress?: number
  message?: string
  error?: string
  queue_progress: number
  queue_status?: StepStatus
}

export function useTaskProgress() {
  const [activeQueue, setActiveQueue] = useState<TaskQueue | null>(null)
  const [recentQueues, setRecentQueues] = useState<TaskQueue[]>([])

  // Handle queue created event
  useEffect(() => {
    const unsubscribe = wsService.on('task_queue_created', (message: WebSocketMessage) => {
      const data = message.data as unknown as TaskQueueData
      const queue: TaskQueue = {
        id: data.id,
        title: data.title,
        steps: data.steps || [],
        status: data.status,
        progress: data.progress,
        conversation_id: data.conversation_id,
      }
      setActiveQueue(queue)
    })

    return unsubscribe
  }, [])

  // Handle queue update event
  useEffect(() => {
    const unsubscribe = wsService.on('task_queue_update', (message: WebSocketMessage) => {
      const data = message.data as unknown as TaskQueueData
      setActiveQueue((prev) => {
        if (!prev || prev.id !== data.id) {
          return {
            id: data.id,
            title: data.title,
            steps: data.steps || [],
            status: data.status,
            progress: data.progress,
            conversation_id: data.conversation_id,
          }
        }
        return {
          ...prev,
          steps: data.steps || prev.steps,
          status: data.status,
          progress: data.progress,
        }
      })
    })

    return unsubscribe
  }, [])

  // Handle step started event
  useEffect(() => {
    const unsubscribe = wsService.on('task_step_started', (message: WebSocketMessage) => {
      const data = message.data as unknown as StepEventData
      setActiveQueue((prev) => {
        if (!prev || prev.id !== data.queue_id) return prev
        if (!data.step) return prev

        const stepExists = prev.steps.some((s) => s.id === data.step!.id)
        const steps = stepExists
          ? prev.steps.map((s) => (s.id === data.step!.id ? data.step! : s))
          : [...prev.steps, data.step]

        return {
          ...prev,
          steps,
          progress: data.queue_progress,
        }
      })
    })

    return unsubscribe
  }, [])

  // Handle step progress event
  useEffect(() => {
    const unsubscribe = wsService.on('task_step_progress', (message: WebSocketMessage) => {
      const data = message.data as unknown as StepEventData
      setActiveQueue((prev) => {
        if (!prev || prev.id !== data.queue_id) return prev

        return {
          ...prev,
          steps: prev.steps.map((s) =>
            s.id === data.step_id
              ? { ...s, progress: data.progress || s.progress, message: data.message || s.message }
              : s
          ),
          progress: data.queue_progress,
        }
      })
    })

    return unsubscribe
  }, [])

  // Handle step completed event
  useEffect(() => {
    const unsubscribe = wsService.on('task_step_completed', (message: WebSocketMessage) => {
      const data = message.data as unknown as StepEventData
      setActiveQueue((prev) => {
        if (!prev || prev.id !== data.queue_id) return prev
        if (!data.step) return prev

        const updatedQueue: TaskQueue = {
          ...prev,
          steps: prev.steps.map((s) => (s.id === data.step!.id ? data.step! : s)),
          status: data.queue_status || prev.status,
          progress: data.queue_progress,
        }

        // If queue is complete, move to recent (but don't auto-hide - wait for chat response)
        if (data.queue_status === 'completed') {
          setRecentQueues((recent) => [updatedQueue, ...recent.slice(0, 4)])
          // Don't auto-hide here - the clearActiveQueue will be called when chat response arrives
        }

        return updatedQueue
      })
    })

    return unsubscribe
  }, [])

  // Handle step failed event
  useEffect(() => {
    const unsubscribe = wsService.on('task_step_failed', (message: WebSocketMessage) => {
      const data = message.data as unknown as StepEventData
      setActiveQueue((prev) => {
        if (!prev || prev.id !== data.queue_id) return prev
        if (!data.step) return prev

        return {
          ...prev,
          steps: prev.steps.map((s) => (s.id === data.step!.id ? data.step! : s)),
          progress: data.queue_progress,
        }
      })
    })

    return unsubscribe
  }, [])

  // Clear active queue (for manual dismissal)
  const clearActiveQueue = useCallback(() => {
    setActiveQueue(null)
  }, [])

  return {
    activeQueue,
    recentQueues,
    clearActiveQueue,
    hasActiveTask: activeQueue !== null && activeQueue.status === 'in_progress',
  }
}

export default useTaskProgress
